import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleNotch, UploadSimple, Check } from "@phosphor-icons/react";
import axios from "axios";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOURS = [
  { name: "Red", hex: "#DC2626" },
  { name: "Maroon", hex: "#7F1D1D" },
  { name: "Orange", hex: "#EA580C" },
  { name: "Yellow", hex: "#EAB308" },
  { name: "Green", hex: "#16A34A" },
  { name: "Dark Green", hex: "#166534" },
  { name: "Teal", hex: "#0D9488" },
  { name: "Sky Blue", hex: "#0EA5E9" },
  { name: "Blue", hex: "#2563EB" },
  { name: "Navy", hex: "#1E3A5F" },
  { name: "Purple", hex: "#7C3AED" },
  { name: "Pink", hex: "#EC4899" },
  { name: "Black", hex: "#0A0A0A" },
  { name: "Grey", hex: "#6B7280" },
  { name: "White", hex: "#FFFFFF" },
];

const CURRENCIES = [
  { label: "£ (GBP)", value: "£" },
  { label: "€ (EUR)", value: "€" },
];

export default function OnboardPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    club_name: "",
    contact_name: "",
    email: "",
    phone: "",
    primary_colour: "",
    secondary_colour: "",
    entry_fee: "",
    currency: "£",
    prize_details: "",
    region: "uk",
  });
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.primary_colour) {
      setError("Please select your club's primary colour.");
      return;
    }
    if (!agreed) {
      setError("Please agree to the Terms and Conditions to continue.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      // Step 1: Submit onboarding details
      const formData = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v) formData.append(k, v);
      });
      if (logo) formData.append("logo", logo);

      const res = await axios.post(`${API}/onboard`, formData);
      const onboardId = res.data.onboard_id;

      // Step 2: Create Stripe checkout session
      const origin = window.location.origin;
      const checkoutRes = await axios.post(`${API}/checkout/session`, {
        onboard_id: onboardId,
        origin_url: origin,
      });

      // Step 3: Redirect to Stripe
      window.location.href = checkoutRes.data.url;
    } catch (err) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const inputClass =
    "w-full bg-white border border-black/10 text-[#0A0A0A] px-4 py-3 font-body text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] focus:border-[#0A0A0A] placeholder:text-[#BBB] transition-colors";

  const regionPkg = form.region === "ie" ? "€249" : "£195";

  return (
    <div className="min-h-screen bg-[#FAFAFA] py-16 px-6">
      <div className="max-w-2xl mx-auto">
        <a href="/" className="font-heading font-black text-xl uppercase tracking-tight text-[#1D3557] inline-block mb-10 flex items-center gap-2">
          <Logo size={28} />
          target<span className="text-[#E63946]">funder</span><span className="text-[#1D3557]/30">.com</span>
        </a>

        <h1 className="font-heading font-bold text-3xl sm:text-4xl uppercase tracking-tight text-[#0A0A0A] mb-2" data-testid="onboard-title">
          Set Up Your Club
        </h1>
        <p className="font-body text-base text-[#666] mb-8 leading-relaxed">
          Fill in your details below and complete payment. We'll have your branded fundraiser live within 24 hours.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6" data-testid="onboard-form">
          {/* Club details */}
          <div className="space-y-4">
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#999]">Club Details</p>

            <div>
              <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#888] mb-1.5">Club Name *</label>
              <input type="text" name="club_name" required value={form.club_name} onChange={handleChange} placeholder="e.g. Ballymore FC" className={inputClass} data-testid="onboard-club-name" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#888] mb-1.5">Contact Name *</label>
                <input type="text" name="contact_name" required value={form.contact_name} onChange={handleChange} placeholder="Your full name" className={inputClass} data-testid="onboard-contact-name" />
              </div>
              <div>
                <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#888] mb-1.5">Email *</label>
                <input type="email" name="email" required value={form.email} onChange={handleChange} placeholder="you@yourclub.com" className={inputClass} data-testid="onboard-email" />
              </div>
            </div>

            <div>
              <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#888] mb-1.5">Phone (optional)</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+44 / +353" className={inputClass} data-testid="onboard-phone" />
            </div>
          </div>

          {/* Logo upload */}
          <div>
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Club Logo</p>
            <label className="flex items-center gap-3 cursor-pointer border border-dashed border-black/15 bg-white p-4 hover:border-black/30 transition-colors" data-testid="onboard-logo-upload">
              <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
              {logoPreview ? (
                <img src={logoPreview} alt="Logo preview" className="w-12 h-12 object-contain" />
              ) : (
                <UploadSimple size={24} className="text-[#BBB]" />
              )}
              <span className="font-body text-sm text-[#888]">
                {logo ? logo.name : "Click to upload your club logo (PNG, JPG, SVG)"}
              </span>
            </label>
            <p className="font-body text-xs text-[#AAA] mt-1.5">Max 5MB. A screenshot from your club's website or social media works fine.</p>
          </div>

          {/* Primary colour */}
          <div>
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Primary Club Colour *</p>
            <div className="flex flex-wrap gap-2" data-testid="onboard-colour-picker">
              {COLOURS.map((c) => (
                <button
                  type="button"
                  key={c.hex}
                  onClick={() => setForm({ ...form, primary_colour: c.hex })}
                  className={`w-10 h-10 flex items-center justify-center transition-all ${
                    c.hex === "#FFFFFF" ? "border border-black/15" : ""
                  } ${
                    form.primary_colour === c.hex
                      ? "ring-2 ring-[#0A0A0A] ring-offset-2"
                      : "hover:scale-110"
                  }`}
                  style={{ backgroundColor: c.hex }}
                  title={c.name}
                  data-testid={`colour-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {form.primary_colour === c.hex && (
                    <Check size={16} weight="bold" className={c.hex === "#FFFFFF" || c.hex === "#EAB308" ? "text-black" : "text-white"} />
                  )}
                </button>
              ))}
            </div>
            {form.primary_colour && (
              <p className="font-body text-xs text-[#999] mt-2">Selected: {form.primary_colour}</p>
            )}
          </div>

          {/* Secondary colour */}
          <div>
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Secondary Club Colour (optional)</p>
            <div className="flex flex-wrap gap-2" data-testid="onboard-secondary-colour-picker">
              {COLOURS.map((c) => (
                <button
                  type="button"
                  key={c.hex}
                  onClick={() => setForm({ ...form, secondary_colour: form.secondary_colour === c.hex ? "" : c.hex })}
                  className={`w-10 h-10 flex items-center justify-center transition-all ${
                    c.hex === "#FFFFFF" ? "border border-black/15" : ""
                  } ${
                    form.secondary_colour === c.hex
                      ? "ring-2 ring-[#0A0A0A] ring-offset-2"
                      : "hover:scale-110"
                  }`}
                  style={{ backgroundColor: c.hex }}
                  title={c.name}
                  data-testid={`secondary-colour-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {form.secondary_colour === c.hex && (
                    <Check size={16} weight="bold" className={c.hex === "#FFFFFF" || c.hex === "#EAB308" ? "text-black" : "text-white"} />
                  )}
                </button>
              ))}
            </div>
            {form.secondary_colour && (
              <p className="font-body text-xs text-[#999] mt-2">Selected: {form.secondary_colour}</p>
            )}
          </div>

          {/* Entry fee */}
          <div>
            <p className="font-body text-xs font-bold uppercase tracking-[0.2em] text-[#999] mb-3">Entry Fee</p>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#888] mb-1.5">Currency *</label>
                <select
                  name="currency"
                  value={form.currency}
                  onChange={(e) => {
                    const currency = e.target.value;
                    setForm({ ...form, currency, region: currency === "€" ? "ie" : "uk" });
                  }}
                  className={inputClass}
                  data-testid="onboard-currency"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#888] mb-1.5">Amount per entry *</label>
                <input type="number" name="entry_fee" required min="1" step="0.01" value={form.entry_fee} onChange={handleChange} placeholder="e.g. 10" className={inputClass} data-testid="onboard-entry-fee" />
              </div>
            </div>
          </div>

          {/* Prize details */}
          <div>
            <label className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-[#888] mb-1.5">Prize Details (optional)</label>
            <textarea name="prize_details" rows={2} value={form.prize_details} onChange={handleChange} placeholder="e.g. 1st: €200, 2nd: €100, 3rd: €50" className={`${inputClass} resize-none`} data-testid="onboard-prize-details" />
            <p className="font-body text-xs text-[#AAA] mt-1.5">Don't worry if you haven't decided yet — we can add these later.</p>
          </div>

          {error && <p className="text-red-500 text-sm font-body" data-testid="onboard-error">{error}</p>}

          {/* Terms agreement */}
          <div className="flex items-start gap-3" data-testid="onboard-terms">
            <button
              type="button"
              onClick={() => setAgreed(!agreed)}
              className={`mt-0.5 flex-shrink-0 w-5 h-5 border flex items-center justify-center transition-colors ${
                agreed ? "bg-[#0A0A0A] border-[#0A0A0A]" : "bg-white border-black/20"
              }`}
              data-testid="onboard-terms-checkbox"
            >
              {agreed && <Check size={12} weight="bold" className="text-white" />}
            </button>
            <p className="font-body text-xs text-[#666] leading-relaxed">
              I am authorised to enter into this agreement on behalf of{" "}
              <strong className="text-[#0A0A0A]">{form.club_name || "my club"}</strong>.
              I have read and agree to the{" "}
              <a href="/terms" target="_blank" className="text-[#0A0A0A] underline hover:text-[#555]">
                TargetFunder Service Terms and Conditions and Data Processing Agreement
              </a>.
            </p>
          </div>

          {/* Payment summary + submit */}
          <div className="border-t border-black/5 pt-6">
            <div className="flex items-center justify-between mb-4">
              <span className="font-body text-sm text-[#666]">Setup fee ({form.region === "ie" ? "Ireland" : "UK"})</span>
              <span className="font-heading font-bold text-2xl text-[#0A0A0A]">{regionPkg}</span>
            </div>
            <p className="font-body text-xs text-[#AAA] mb-4">One-time payment. No subscriptions. No commissions.</p>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A0A0A] text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#222] transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="onboard-submit-btn"
            >
              {loading ? (
                <CircleNotch size={18} className="animate-spin" />
              ) : (
                `Pay ${regionPkg} & Get Started`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
